/**
 * 设计令牌的 TypeScript 包装层。
 *
 * 唯一真相来源：docs/design-system/design-tokens.json（值）+ design-tokens.css（运行时变量）。
 * 本文件**不重新定义任何色值**——只把 JSON 中的语义映射提升为带类型的常量，
 * 并在模块加载时做一次完整性断言，使「文档改了但代码没跟上」在启动瞬间就炸掉，
 * 而不是在某个状态标签上悄悄渲染错颜色（失效模式 #2 沉默逻辑错误）。
 */
import tokensJson from '@tokens/design-tokens.json';
import type { IconName } from './icons/registry';
import { LICENSE_CODE_STATUSES, LICENSE_STATUSES } from './api/types';
import type { LicenseCodeStatus, LicenseStatus } from './api/types';

/* ── 状态枚举来自契约层（api/types.ts ← openapi.yaml），此处不重复定义 ── */

export type AnyStatus = LicenseCodeStatus | LicenseStatus;

/* ── 令牌名（只允许引用这些名字，组件层禁止裸 hex） ── */

export type StatusToken =
  | 'st-idle'
  | 'st-hold'
  | 'st-transit'
  | 'st-done'
  | 'st-void'
  | 'st-live'
  | 'st-danger'
  | 'st-expired';

export type SemanticToken = 'success' | 'warn' | 'danger' | 'info' | 'accent';

export type TokenName =
  | StatusToken
  | SemanticToken
  | 'bg'
  | 'surface'
  | 'surface-2'
  | 'surface-sunken'
  | 'fg'
  | 'fg-2'
  | 'muted'
  | 'meta'
  | 'border'
  | 'border-strong'
  | 'border-soft'
  | 'border-control'
  | 'accent-on'
  | 'accent-hover'
  | 'accent-subtle';

/** 组件层取色的唯一入口：`color: cssVar('st-live')` → `var(--st-live)` */
export function cssVar(name: TokenName): string {
  return `var(--${name})`;
}

/* ── 图标尺寸四档（docs/03-UIUX.md §7.2；禁止其他尺寸） ── */

export const ICON_SIZE = {
  /** 12px：状态 badge 内、表格行内次级标记 */
  badge: 12,
  /** 16px：行内、表格操作、输入框前后缀、提示条 */
  inline: 16,
  /** 20px：按钮内图标、侧边栏导航项 */
  control: 20,
  /** 24px：独立图标、对话框标题、空状态 */
  standalone: 24,
} as const;

export type IconSize = (typeof ICON_SIZE)[keyof typeof ICON_SIZE];

/* ── 状态描述符（label / 色 Token / 图标 三通道，来自 JSON） ── */

export interface StatusDescriptor {
  readonly track: 'A' | 'B';
  readonly value: AnyStatus;
  readonly label: string;
  readonly token: StatusToken;
  readonly icon: IconName;
  /** 仅 void：第四通道删除线 */
  readonly extra?: 'line-through';
}

interface DesignTokensShape {
  readonly status: {
    readonly map: ReadonlyArray<{
      track: string;
      value: string;
      label: string;
      token: string;
      icon: string;
      extra?: string;
    }>;
  };
  readonly icon: {
    readonly library: string;
    readonly lockedVersion: string;
    readonly sizes: ReadonlyArray<{ px: number; strokeWidth: number; use: string }>;
  };
}

const raw = tokensJson as unknown as DesignTokensShape;

/* ── 描边宽度：唯一来源是 design-tokens.json 的 icon.sizes（OD-019） ──
   本文件**不写任何字面量描边值**。历史上这里硬编码过 `size === 12 ? 2.25 : 2`，
   于是 JSON、design-tokens.css、03-UIUX §3.2 三处各写各的，§3.2 一度写成 2。
   现在改为从 JSON 派生 + 启动断言：令牌文件改了而代码没跟上，会在加载瞬间抛错，
   而不是让某档图标悄悄渲染错描边（失效模式 #2 沉默逻辑错误）。 */

function buildStrokeMap(): ReadonlyMap<number, number> {
  const map = new Map<number, number>();
  for (const entry of raw.icon.sizes) {
    if (!Number.isFinite(entry.px) || !Number.isFinite(entry.strokeWidth)) {
      throw new Error(
        `design-tokens.json icon.sizes 存在非数值条目：${JSON.stringify(entry)}。描边宽度必须可计算。`,
      );
    }
    map.set(entry.px, entry.strokeWidth);
  }

  const ladder = Object.values(ICON_SIZE);
  const missing = ladder.filter((px) => !map.has(px));
  if (missing.length > 0) {
    throw new Error(
      `design-tokens.json icon.sizes 缺少尺寸档 ${missing.join(' / ')}px。` +
        '四档尺寸阶梯是 docs/03-UIUX.md §7.2 的 LOCKED 内容，缺档会导致该档图标拿不到描边宽度。',
    );
  }
  const extra = [...map.keys()].filter((px) => !ladder.includes(px as IconSize));
  if (extra.length > 0) {
    throw new Error(
      `design-tokens.json icon.sizes 出现四档之外的尺寸 ${extra.join(' / ')}px。` +
        '新增档位必须先改 docs/03-UIUX.md §7.2 与 docs/06-ICON-MAP.md §1.1，再改代码。',
    );
  }
  return map;
}

const STROKE_BY_PX = buildStrokeMap();

/** 各档描边宽度（值来自 design-tokens.json，不在代码里重复定义） */
export const ICON_STROKE: Readonly<Record<IconSize, number>> = Object.freeze(
  Object.fromEntries(
    Object.values(ICON_SIZE).map((px) => [px, STROKE_BY_PX.get(px)]),
  ) as Record<IconSize, number>,
);

export function strokeWidthFor(size: IconSize): number {
  return ICON_STROKE[size];
}

/**
 * 把描边宽度暴露成 CSS 变量，供样式表引用 `var(--icon-stroke)` 而非字面量。
 *
 * - `--icon-stroke-12 / -16 / -20 / -24`：逐档
 * - `--icon-stroke`：状态 badge 档（12px）的别名，03-UIUX §3.2 的 `.status-badge > svg` 引用它
 *
 * 之所以在运行时注入而不是往 CSS 里再写一遍：docs/design-system/design-tokens.json
 * 是唯一来源，任何「再写一遍」都是漂移的起点。
 */
export function installIconStrokeVars(root: HTMLElement): void {
  for (const [px, width] of STROKE_BY_PX) {
    root.style.setProperty(`--icon-stroke-${String(px)}`, String(width));
  }
  root.style.setProperty('--icon-stroke', String(ICON_STROKE[ICON_SIZE.badge]));
}

function buildStatusMap(): Readonly<Record<AnyStatus, StatusDescriptor>> {
  const out: Partial<Record<AnyStatus, StatusDescriptor>> = {};
  for (const entry of raw.status.map) {
    out[entry.value as AnyStatus] = {
      track: entry.track === 'A' ? 'A' : 'B',
      value: entry.value as AnyStatus,
      label: entry.label,
      token: entry.token as StatusToken,
      icon: entry.icon as IconName,
      ...(entry.extra === 'line-through' ? { extra: 'line-through' as const } : {}),
    };
  }

  const expected: AnyStatus[] = [...LICENSE_CODE_STATUSES, ...LICENSE_STATUSES];
  const missing = expected.filter((value) => out[value] === undefined);
  if (missing.length > 0) {
    throw new Error(
      `design-tokens.json 的 status.map 缺少状态：${missing.join(', ')}。` +
        '状态映射是 docs/06-ICON-MAP.md §3 的 LOCKED 内容，缺项会导致状态标签渲染不出图标与色，必须先修复令牌文件。',
    );
  }
  return out as Record<AnyStatus, StatusDescriptor>;
}

export const STATUS_MAP: Readonly<Record<AnyStatus, StatusDescriptor>> = buildStatusMap();

export function statusOf(value: AnyStatus): StatusDescriptor {
  return STATUS_MAP[value];
}

/** 锁定的图标库标识，用于启动自检与关于页展示 */
export const ICON_LIBRARY = `${raw.icon.library}@${raw.icon.lockedVersion}`;
